import React from 'react';
import { Glyph } from './glyphs';

// windowMs must be a NUMBER: the runner derives its reaction-time ceiling as
// `timing.windowMs + 250`, so a string window would concatenate ("4000" + 250)
// and every response would be discarded as out of range.
const DEFAULT_WINDOW_MS = 4000;
const DEFAULT_ISI_MS = 1000;
const FIXATION_MS = 300;

// The table is served per block on params.code_table and regenerated per run.
// It is read straight off params on every render — never cached, never
// defaulted. A wrong table would silently invalidate every trial in the block.
function codeTable(params) {
  const table = params?.code_table;
  if (!table || typeof table !== 'object') return null;
  // Display order is digit-ascending regardless of the order the keys were
  // served in. This orders the on-screen AID only — nothing here is indexed by
  // position, so it cannot remap an answer.
  const entries = Object.entries(table).sort((a, b) => Number(a[0]) - Number(b[0]));
  return entries.length ? entries : null;
}

// Stays on screen for the whole block, across fixation and gap as well as the
// stimulus. Looking the pair up is the thing being measured; hiding the table
// between trials would turn this into a memory task.
function BlockContextView({ params }) {
  const entries = codeTable(params);
  if (!entries) return null;
  return (
    <div
      style={{
        display: 'flex',
        gap: 'var(--space-2)',
        flexWrap: 'wrap',
        justifyContent: 'center',
        background: 'var(--glass-bg-light)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-3) var(--space-4)',
      }}
    >
      {entries.map(([digit, symbol]) => (
        <div
          key={digit}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 'var(--space-1)',
            minWidth: 34,
          }}
        >
          <span
            style={{
              fontSize: 'var(--text-panel-size)',
              fontWeight: 800,
              color: 'var(--text-primary)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {digit}
          </span>
          <Glyph name={symbol} size={24} />
        </div>
      ))}
    </div>
  );
}

// Draws the served pair and nothing else. It is never handed the code table, so
// it cannot mark the pair as matching or not — judging the pair is the task.
// The glyph is aria-hidden, so without a label a screen reader would hear the
// digit alone; the label names both halves of the pair, which are both served.
function StimulusView({ stimulus, compact }) {
  return (
    <div
      role="img"
      aria-label={`digit ${stimulus.digit} paired with ${stimulus.symbol}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: compact ? 8 : 24,
        border: '1px solid var(--border-strong)',
        borderRadius: compact ? 'var(--radius-control)' : 'var(--radius-lg)',
        padding: compact ? 'var(--space-2) var(--space-3)' : 'var(--space-4) var(--space-6)',
      }}
    >
      <span
        style={{
          fontSize: compact ? 22 : 64,
          fontWeight: 800,
          lineHeight: 1,
          color: 'var(--text-primary)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {stimulus.digit}
      </span>
      <Glyph name={stimulus.symbol} size={compact ? 24 : 64} />
    </div>
  );
}

export const digitSymbolTask = {
  id: 'digit-symbol',
  name: 'Code Check',
  pillar: 'speed',

  // Forced choice: every trial ends in a response, so trial length carries no
  // information about what the participant did and there is no rhythm to leak.
  // The trial may end as soon as an answer lands.
  holdsFullWindow: false,

  instructions: [
    'A code table stays on screen the whole round, pairing each digit with a symbol.',
    'Each trial shows one digit and one symbol.',
    'Answer whether that pair matches the table. Look it up — you do not need to memorise it.',
  ],
  // Negative LEFT, affirmative RIGHT — the battery-wide convention, so a
  // participant moving between instruments never has to re-learn which side
  // means "yes". ResponseTargets renders this array left-to-right with no
  // reordering, so the keys and hint belong to the POSITION, not to the id:
  // ArrowLeft/f must always sit on the left button whatever it answers.
  targets: [
    { id: 'wrong', label: "Doesn't match", keys: ['ArrowLeft', 'f'], hint: '←' },
    { id: 'correct', label: 'Matches', keys: ['ArrowRight', 'j'], hint: '→' },
  ],

  // The examples table has no block params, so it illustrates the pair only.
  // Listed in target order, so the card reads in the same sequence as the two
  // buttons underneath it.
  examples: [
    { stimulus: { digit: 4, symbol: 'star' }, response: "Doesn't match", note: 'if the table pairs 4 with something else' },
    { stimulus: { digit: 4, symbol: 'zigzag' }, response: 'Matches', note: 'if the table pairs 4 with this symbol' },
  ],

  // Reports the JUDGEMENT the participant made, not the symbol they saw. The
  // server already knows the symbol; it does not know what the user concluded.
  toAnswer: (targetId) => ({ isCorrectPair: targetId === 'correct' }),

  // symbol_count is the difficulty knob. It is already implied by the table on
  // screen, so it is not rendered separately.
  //
  // The fixation sits ON TOP of the served isi for this instrument — it is not
  // subtracted from the gap the way flanker and stroop do it. That difference
  // is mandated per-instrument; do not harmonise it.
  timing: (params) => ({
    fixationMs: FIXATION_MS,
    windowMs: Number(params?.response_window_ms) || DEFAULT_WINDOW_MS,
    gapMs: Number(params?.isi_ms) || DEFAULT_ISI_MS,
  }),

  // Practice only. The table is on screen, so this reads what the participant
  // reads. String-keyed because the table arrives as JSON object keys.
  practiceExpectation: (stimulus, ctx) => {
    const table = ctx?.params?.code_table;
    if (!table) return null;
    return table[String(stimulus.digit)] === stimulus.symbol ? 'correct' : 'wrong';
  },

  BlockContextView,
  StimulusView,
};
